import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import Logo from "../components/argus/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@argus.security");
  const [password, setPassword] = useState("DemoPass!23");
  // (Brand: ArgusSecure)
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/app");
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      let msg;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = `${d[0].msg}${d[0].loc ? ` (${d[0].loc.slice(-1)})` : ""}`;
      else if (ex?.response?.status === 429) msg = "Too many login attempts from this network. Wait 60 seconds and try again.";
      else if (ex?.response?.status === 423) msg = "Account temporarily locked after multiple failed attempts. Try again in 15 minutes, or reset your password.";
      else if (ex?.response?.status === 0 || !ex?.response) msg = "Can't reach the ArgusSecure API. Check your connection and try again.";
      else msg = "That didn't work — double-check email and password.";
      setErr(msg);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-paper">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-10">
            <Logo size={28} />
          </Link>
          <h1 className="font-display text-4xl mb-2" data-testid="login-title">Welcome back.</h1>
          <p className="text-ink/60 mb-8">Sign in to keep finding it before they do.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form" noValidate aria-describedby={err ? "login-error" : undefined}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 mt-1" data-testid="login-email" aria-required="true" aria-invalid={err && /email|password|wrong/i.test(err) ? "true" : undefined} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-gold hover:underline" data-testid="login-forgot-link">
                  Forgot?
                </Link>
              </div>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 mt-1" data-testid="login-password" aria-required="true" aria-invalid={err && /password|wrong/i.test(err) ? "true" : undefined} />
            </div>

            {err && (
              <div
                id="login-error"
                role="alert"
                aria-live="assertive"
                className="text-sm text-sev-critical bg-sev-critical/5 border border-sev-critical/20 rounded-md p-3"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full h-12 bg-gold text-paper hover:bg-gold2 font-semibold" data-testid="login-submit" aria-busy={loading ? "true" : "false"}>
              {loading ? "Signing you in…" : "Sign In"} <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </Button>
          </form>

          <p className="text-sm text-ink/60 mt-6">
            New to ArgusSecure? <Link to="/signup" className="text-gold hover:underline" data-testid="login-signup-link">Start a free scan →</Link>
          </p>

          <div className="mt-10 p-4 rounded-lg bg-raised/40 border border-line text-xs text-ink/70" data-testid="demo-callout">
            <strong className="text-ink">Try the demo:</strong> <code className="font-mono">demo@argus.security</code> · <code className="font-mono">DemoPass!23</code>
          </div>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 bg-panel text-ink relative overflow-hidden border-l border-line">
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <ShieldCheck size={36} className="text-gold" />
          <div>
            <p className="font-display text-4xl leading-tight">
              Hackers Are <span className="italic">Already</span> Looking. <span className="text-gold">Look First.</span>
            </p>
            <p className="mt-4 text-ink/60 text-sm">
              Continuous SAST, DAST and SCA. Evidence on every finding. A fix beside every flaw.
            </p>
          </div>
          <div className="text-xs text-ink/40">© 2026 ArgusSecure, a GSGD FIELD SOLUTIONS ENGINEERING product.</div>
        </div>
      </div>
    </div>
  );
}
